import { Link, useLocation } from 'react-router-dom';
import { Badge, Button, Layout, Menu, MenuProps, theme } from 'antd';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { menu, MenuItem } from '../../config/Menu';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Logo } from '../../components/Logo';
import { clearAuthToken } from '../../redux/slices/user';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import {
	LogoutOutlined,
	NotificationOutlined,
	QuestionCircleOutlined,
} from '@ant-design/icons';
import { useWindowSize } from '../../hooks/useWindowSize';
import { version } from '../../../package.json';
import { useGetLoggedInUserQuery } from '../../redux/api/users';
import { useGetProductionQuestionsQuery } from '../../redux/api/production-questions';
import type { MenuBadgeKey } from '../../config/Menu';
import { UserGuideModal, MAIN_SECTIONS } from '../../features/user-guide';
import {
	UpdatesModal,
	UpdatesTab,
	useUnseenUpdates,
} from '../../features/updates';

const { Sider } = Layout;
type Props = { children: ReactNode };
export default function MainLayout({ children }: Props) {
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const [current, setCurrent] = useState(location.pathname);
	const [openKeys, setOpenKeys] = useState<string[]>([]);
	const [userGuideOpen, setUserGuideOpen] = useState(false);
	const [updatesOpen, setUpdatesOpen] = useState(false);
	// Undefined = let the window pick whichever tab has content. Only the
	// Changelog link pins a tab.
	const [updatesTab, setUpdatesTab] = useState<UpdatesTab | undefined>();

	const {
		items: roadmapItems,
		isLoading: roadmapLoading,
		unseenCount,
		markSeen,
		refresh: refreshUpdates,
	} = useUnseenUpdates();

	const openUpdates = useCallback(
		(tab?: UpdatesTab) => {
			refreshUpdates(); // what the user is about to read should be current
			setUpdatesTab(tab);
			setUpdatesOpen(true);
			markSeen();
		},
		[markSeen, refreshUpdates]
	);

	// Mark again on close: the poll or the open-refetch may have brought new
	// items in while the window was up, and the user has seen those too.
	const closeUpdates = useCallback(() => {
		markSeen();
		setUpdatesOpen(false);
	}, [markSeen]);

	const { width } = useWindowSize();
	const { token } = theme.useToken();
	const { data: loggedInUser } = useGetLoggedInUserQuery();
	const userGroups = loggedInUser?.groups ?? [];

	const { data: notReportedProductionQuestions } =
		useGetProductionQuestionsQuery(
			{ status: 'not_reported', page_size: 1 },
			{ pollingInterval: 60_000 }
		);

	const menuBadgeCounts: Partial<Record<MenuBadgeKey, number>> = useMemo(
		() => ({
			not_reported_production_questions:
				notReportedProductionQuestions?.count ?? 0,
		}),
		[notReportedProductionQuestions?.count]
	);

	const renderMenuLabel = useCallback(
		(item: MenuItem) => {
			if (!item.path) {
				return item.label;
			}

			const badgeCount = item.badge ? menuBadgeCounts[item.badge] ?? 0 : 0;

			return (
				<span className="flex w-full min-w-0 items-center justify-between gap-2">
					{item.external ? (
						// Another host — a router Link would try to route the whole
						// URL as an in-app path and land on the 404 screen.
						<a href={item.path} className="min-w-0 flex-1 truncate">
							{item.label}
						</a>
					) : (
						<Link to={item.path} className="min-w-0 flex-1 truncate">
							{item.label}
						</Link>
					)}
					{badgeCount > 0 ? (
						<Badge
							count={badgeCount}
							size="small"
							overflowCount={99}
							style={{ boxShadow: '0 0 0 1.5px #fff' }}
						/>
					) : null}
				</span>
			);
		},
		[menuBadgeCounts]
	);

	/** Collect all leaf paths from a menu item (recursively). */
	function collectPaths(item: MenuItem): string[] {
		if (item.path) return [item.path];
		if (!item.children) return [];
		return item.children.flatMap(collectPaths);
	}

	/** Find the chain of parent labels for a given path (for openKeys). */
	function findParentKeys(
		items: MenuItem[],
		path: string,
		parents: string[] = []
	): string[] | null {
		for (const item of items) {
			if (item.path === path) return parents;
			if (item.children) {
				const found = findParentKeys(item.children, path, [
					...parents,
					item.label,
				]);
				if (found) return found;
			}
		}
		return null;
	}

	/** Collect all submenu keys (labels of items with children). */
	function collectSubmenuKeys(items: MenuItem[]): string[] {
		const keys: string[] = [];
		for (const item of items) {
			if (item.children) {
				keys.push(item.label);
				keys.push(...collectSubmenuKeys(item.children));
			}
		}
		return keys;
	}

	/**
	 * Apply `requiredGroup` at every depth, not just the top level: a single
	 * restricted entry usually lives inside an otherwise open section. A parent
	 * whose children are all filtered out goes too, rather than leaving a
	 * submenu that opens onto nothing.
	 */
	const filterByGroup = useCallback(
		(items: MenuItem[]): MenuItem[] =>
			items.reduce<MenuItem[]>((allowed, item) => {
				if (item.requiredGroup && !userGroups.includes(item.requiredGroup)) {
					return allowed;
				}
				if (item.visible && !item.visible()) {
					return allowed;
				}
				if (!item.children) {
					allowed.push(item);
					return allowed;
				}
				const children = filterByGroup(item.children);
				if (children.length) {
					allowed.push({ ...item, children });
				}
				return allowed;
			}, []),
		[userGroups]
	);

	const filteredMenu = useMemo(() => filterByGroup(menu), [filterByGroup]);

	const allSubmenuKeys = useMemo(
		() => collectSubmenuKeys(filteredMenu),
		[filteredMenu]
	);

	const sidebarMenuItems = useMemo(() => {
		const mapItem = (item: MenuItem): NonNullable<MenuProps['items']>[number] => {
			if (item.path) {
				return {
					key: item.path,
					label: renderMenuLabel(item),
				};
			}
			return {
				key: item.label,
				label: item.label,
				children: item.children?.map((child) => mapItem(child)),
			};
		};

		return filteredMenu.map((item) => mapItem(item));
	}, [filteredMenu, renderMenuLabel]);

	function handleLogout() {
		dispatch(clearAuthToken());
	}

	const onClick: MenuProps['onClick'] = (e) => {
		setCurrent(e.key);
	};

	const onOpenChange = (keys: string[]) => {
		// Find which top-level key was just opened (the new one not in current openKeys)
		const latestOpen = keys.find((key) => !openKeys.includes(key));

		if (!latestOpen) {
			// A key was closed — just use the new keys
			setOpenKeys(keys);
			return;
		}

		// Check if the latest opened key is a top-level menu item
		const isTopLevel = filteredMenu.some(
			(item) => item.label === latestOpen && item.children
		);

		if (isTopLevel) {
			// Close other top-level menus but keep nested sub-menus within the new one
			const nestedKeys = keys.filter(
				(key) =>
					!allSubmenuKeys.includes(key) ||
					key === latestOpen ||
					!filteredMenu.some((m) => m.label === key)
			);
			setOpenKeys(nestedKeys);
		} else {
			// Opening a nested submenu — keep all keys
			setOpenKeys(keys);
		}
	};

	const extractUUID = (path: string) => {
		const uuidRegex =
			/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
		const match = path.match(uuidRegex);
		return match ? match[0] : null;
	};

	const hideOrShowSider = (path: string) => {
		const sider = document.querySelector('.ant-layout-sider') as HTMLElement;

		if (path.includes('operations')) {
			if (sider) {
				sider.style.display = 'none';
			}
		} else if (sider && sider.style.display === 'none') {
			sider.style.display = 'block';
		}
	};

	useEffect(() => {
		const path = location.pathname;
		const uuid = extractUUID(path);

		if (path) {
			hideOrShowSider(path);
		}

		if (uuid) {
			const substringsToCheck = path
				.split('/')
				.filter((item) => item !== '' && item !== uuid);

			let matchPath: string | undefined;

			filteredMenu.some((item) => {
				const leafPaths = collectPaths(item);
				matchPath = leafPaths.find((lp) => {
					return substringsToCheck.every((substring) => lp.includes(substring));
				});
				return Boolean(matchPath);
			});

			if (matchPath) {
				setCurrent(matchPath);
			}
		} else {
			setCurrent(path);
		}

		// Auto-expand parent menus for the current path
		const parentKeys = findParentKeys(filteredMenu, path);
		if (parentKeys && parentKeys.length > 0) {
			setOpenKeys(parentKeys);
		}
	}, [location.pathname, filteredMenu]);

	return (
		<Layout style={{ height: '100vh', overflow: 'hidden' }}>
			<Layout style={{ minHeight: 0 }}>
				<Sider
					breakpoint="lg"
					theme="light"
					className="main-sider"
					style={{
						height: '100vh',
						borderInlineEnd: `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`,
					}}
				>
					<div className={'cursor-pointer mt-2'} onClick={() => navigate('/')}>
						<Logo
							style={{
								display: 'flex',
								margin: 'auto',
								width: '140px',
							}}
						/>
					</div>
					<Scrollbars
						className="main-sider-menu"
						style={{ flex: 1, minHeight: 0 }}
						hideTracksWhenNotNeeded
						renderTrackHorizontal={() => <div />}
						renderTrackVertical={({ style, ...props }) => (
							<div
								{...props}
								className="main-sider-menu-track"
								style={{ ...style, top: 2, right: 2, bottom: 2, borderRadius: 3 }}
							/>
						)}
					>
						<Menu
							theme="light"
							mode="inline"
							onClick={onClick}
							selectedKeys={[current]}
							openKeys={openKeys}
							onOpenChange={onOpenChange}
							items={sidebarMenuItems}
							style={{ borderInlineEnd: 'none' }}
						/>
					</Scrollbars>
					<div style={{ padding: '8px 16px', borderTop: '1px solid #f0f0f0' }}>
						{/* The Badge — not the Button — carries the width and the
						    bottom margin: it is the element in the footer's flow,
						    so putting the spacing on the Button inside it left
						    this row sitting differently from the two below. */}
						<Badge
							count={unseenCount}
							size="small"
							offset={[-4, 2]}
							rootClassName="block w-full"
							style={{ boxShadow: '0 0 0 1.5px #fff' }}
							styles={{
								root: { display: 'block', width: '100%', marginBottom: 6 },
							}}
						>
							<Button style={{ width: '100%' }} onClick={() => openUpdates()}>
								{width && width <= 992 ? (
									<NotificationOutlined />
								) : (
									<>
										<NotificationOutlined /> What's New
									</>
								)}
							</Button>
						</Badge>
						<Button
							style={{ width: '100%', marginBottom: 6 }}
							onClick={() => setUserGuideOpen(true)}
						>
							{width && width <= 992 ? (
								<QuestionCircleOutlined />
							) : (
								<>
									<QuestionCircleOutlined /> User Guide
								</>
							)}
						</Button>
						<Button style={{ width: '100%' }} onClick={handleLogout}>
							{width && width <= 992 ? (
								<LogoutOutlined />
							) : (
								<>
									<LogoutOutlined /> Log Out
								</>
							)}
						</Button>
						<div
							style={{
								textAlign: 'center',
								fontSize: '10px',
								marginTop: '5px',
							}}
						>
							{version}
							<br />
							<a
								onClick={() => openUpdates('new')}
								style={{
									cursor: 'pointer',
									color: '#1677ff',
									fontSize: '10px',
								}}
							>
								Changelog
							</a>
						</div>
					</div>
				</Sider>
				{/* minHeight:0 + flex:1 keep this pane the scrollport under the
				    100vh shell. Children use min-h-full (not h-full) so tall
				    document-flow pages grow scrollHeight; short fill-viewport
				    pages still stretch to the pane via flex align-items. */}
				<Layout style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
					<div className="flex w-full justify-around min-h-full">
						{children}
					</div>
				</Layout>
			</Layout>
			<UserGuideModal
				open={userGuideOpen}
				onClose={() => setUserGuideOpen(false)}
				sections={MAIN_SECTIONS}
				portal="main"
				manualTitle="Fastlane ERP — User Manual"
			/>
			<UpdatesModal
				open={updatesOpen}
				onClose={closeUpdates}
				items={roadmapItems}
				isLoading={roadmapLoading}
				initialTab={updatesTab}
			/>
		</Layout>
	);
}
